import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useAuth } from "context/AuthContext";

const ZendeskCallbackHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setZendeskSubdomain, checkAuthenticationStatus } = useAuth();

  useEffect(() => {
    console.log('🔄 ZendeskCallbackHandler: Callback received');
    const params = new URLSearchParams(location.search);
    const code = params.get("code");
    const error = params.get("error");
    const subdomain = localStorage.getItem("zendesk_subdomain");
    
    console.log('🔍 ZendeskCallbackHandler: Code:', code ? 'Present' : 'Missing');
    console.log('🔍 ZendeskCallbackHandler: Error:', error || 'None');
    console.log('🔍 ZendeskCallbackHandler: Subdomain:', subdomain || 'Missing');

    const fetchUserData = async (accessToken) => {
      try {
        const response = await fetch(
          `https://${subdomain}.zendesk.com/api/v2/users/me.json`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (!response.ok) throw new Error("Failed to fetch user data");
        return await response.json();
      } catch (err) {
        console.error("Error fetching user data:", err);
        throw err;
      }
    };

    const handleAuth = async () => {
      if (error) {
        toast.error(`Zendesk authentication failed: ${error}`, {
          theme: "colored",
        });
        navigate("/");
        return;
      }

      if (code && subdomain) {
        try {
          // Exchange code for token
          const tokenResponse = await fetch("/api/zendesk/auth", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, subdomain }),
          });

          const tokenData = await tokenResponse.json();

          if (!tokenData.access_token) {
            throw new Error(tokenData.error || "Authentication failed");
          }

          // Fetch user data
          const { user, account } = await fetchUserData(tokenData.access_token);

          // Store all data
          localStorage.setItem("zendesk_token", tokenData.access_token);
          localStorage.setItem("zendesk_user", JSON.stringify(user));
          localStorage.setItem("zendesk_account", JSON.stringify(account));
          
          console.log('✅ ZendeskCallbackHandler: OAuth successful, setting subdomain and checking auth');
          // Set subdomain in AuthContext
          setZendeskSubdomain(subdomain);
          
          // Check authentication status
          const authResult = await checkAuthenticationStatus();
          
          if (authResult) {
            console.log('✅ ZendeskCallbackHandler: Authentication successful, redirecting to landing page');
            // Redirect to landing page with success
            navigate("/landingpage", { state: { authSuccess: true } });
          } else {
            console.log('❌ ZendeskCallbackHandler: Authentication failed, redirecting to login');
            // If authentication fails, redirect to login
            navigate("/", { state: { authError: true } });
          }
        } catch (err) {
          toast.error(`Error: ${err.message}`, { theme: "colored" });
          navigate("/");
        }
      } else {
        toast.error("Missing authentication parameters", { theme: "colored" });
        navigate("/");
      }
    };

    handleAuth();
  }, [location, navigate]);

  return (
    <div className="text-center p-5">
      <Spinner animation="border" size="lg" className="me-2" />
      <h4>Authenticating with Zendesk...</h4>
    </div>
  );
};

export default ZendeskCallbackHandler;
